import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '../../lib/theme';

export interface TrendPoint {
  day: string;
  created: number;
  completed: number;
  cancelled: number;
}

interface Props {
  data: TrendPoint[];
}

function tickDay(day: string): string {
  const d = new Date(day);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short' });
}

export function TrendChart({ data }: Props) {
  const { resolved } = useTheme();
  const dark = resolved === 'dark';

  // Brand greens are theme-invariant. Cancelled uses muted zinc.
  const colors = {
    created: '#16A34A',
    completed: dark ? '#22C55E' : '#15803D',
    cancelled: dark ? '#A1A1AA' : '#71717A',
    grid: dark ? '#262626' : '#E4E4E7',
    axis: dark ? '#909097' : '#71717A',
    tooltipBg: dark ? '#171717' : '#FFFFFF',
    tooltipBorder: dark ? '#262626' : '#E4E4E7',
    text: dark ? '#FAFAFA' : '#0A0A0A',
  };

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradCreated" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={colors.created} stopOpacity={0.30} />
              <stop offset="100%" stopColor={colors.created} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={colors.completed} stopOpacity={0.18} />
              <stop offset="100%" stopColor={colors.completed} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={tickDay}
            tick={{ fill: colors.axis, fontSize: 11 }}
            stroke={colors.grid}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: colors.axis, fontSize: 11 }}
            stroke={colors.grid}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <Tooltip
            cursor={{ stroke: colors.grid, strokeWidth: 1 }}
            contentStyle={{
              background: colors.tooltipBg,
              border: `1px solid ${colors.tooltipBorder}`,
              borderRadius: 10,
              color: colors.text,
              fontSize: 12,
              padding: '8px 12px',
              boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
            }}
            labelFormatter={(label) => tickDay(String(label))}
            labelStyle={{ color: colors.axis, fontSize: 11, marginBottom: 4 }}
          />
          <Area
            type="monotone"
            dataKey="created"
            stroke={colors.created}
            strokeWidth={2}
            fill="url(#gradCreated)"
            name="Created"
          />
          <Area
            type="monotone"
            dataKey="completed"
            stroke={colors.completed}
            strokeWidth={2}
            fill="url(#gradCompleted)"
            name="Completed"
          />
          <Area
            type="monotone"
            dataKey="cancelled"
            stroke={colors.cancelled}
            strokeWidth={1.5}
            strokeDasharray="3 3"
            fill="transparent"
            name="Cancelled"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
